import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from './services/api';
import type { Message, RagDocumentDetails } from './types';
import AgentSuggestionComponent from './components/AgentSuggestion';
import { generateUUID } from './utils/uuid';
import { Sidebar } from './components/Sidebar';
import { VoiceInterface } from './components/VoiceInterface';
import { useAuth } from './contexts/AuthContext';
import { useLupaChat } from './contexts/LupaChatContext';
import {
  DEFAULT_CONVERSATION_TITLE,
  createEmptyConversation,
  deriveConversationTitle,
  sortConversationsByUpdatedAt,
} from './contexts/lupaChatHelpers';
import BrandIcon from './assets/DWA_WolfHead_Favicon_Web.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faFlag } from '@fortawesome/free-regular-svg-icons';
import { faChevronRight, faChevronDown, faBars, faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { FeedbackModal } from './components/FeedbackModal';
import { BetaWelcomeModal } from './components/BetaWelcomeModal';
import { BetaWelcomePanel } from './components/BetaWelcomePanel';
import { TermsModal } from './components/TermsModal';
import { PrivacyModal } from './components/PrivacyModal';
import { SettingsModal } from './components/SettingsModal';
import { ArchivedModal } from './components/ArchivedModal';
import { DocumentViewerModal } from './components/DocumentViewerModal';
import { CitationLink } from './components/CitationLink';

const DARK_COLOR = '#444444';
const ACCENT_COLOR = '#fec35c';
const LUPA_AVATAR_BG = '#000000';
const DEFAULT_BORDER_COLOR = 'rgba(229, 231, 235, 0.5)';
const CHAT_SHELL_BORDER_COLOR = 'rgba(255, 255, 255, 0.3)';
const CARD_SHADOW = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
const FOCUS_RING = `${CARD_SHADOW}, 0 0 0 3px rgba(254, 195, 92, 0.12)`;

// Simple Avatar component
const Avatar: React.FC<{ src?: string; fallback?: string; size?: number; color?: string; borderColor?: string | null }> = ({ 
  src, 
  fallback = 'AI', 
  size = 40,
  color = ACCENT_COLOR,
  borderColor = 'white'
}) => {
  const [imageError, setImageError] = React.useState(false);
  
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: size * 0.4,
      fontWeight: '600',
      border: borderColor ? `2px solid ${borderColor}` : 'none',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      flexShrink: 0,
      overflow: 'hidden'
    }}>
      {(src && !imageError) ? (
         <img
           src={src}
           alt="Avatar"
           style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
           onError={() => setImageError(true)}
         />
       ) : (
         fallback.charAt(0).toUpperCase()
       )}
    </div>
  );
};


// Regex to match citation format: [[ref:azure_search_id]]
const CITATION_REGEX = /\[\[ref:([^\]]+)\]\]/g;
const FOUNDRY_CITATION_PREFIX = 'fd:';

interface FoundryCitationData {
  kind?: string;
  azure_search_id?: string | null;
  source_url?: string | null;
  title?: string | null;
  page_number?: number | null;
}

const parseFoundryCitationId = (citationId: string): FoundryCitationData | null => {
  if (!citationId.startsWith(FOUNDRY_CITATION_PREFIX)) {
    return null;
  }

  try {
    const encoded = citationId.slice(FOUNDRY_CITATION_PREFIX.length);
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const decoded = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(decoded) as FoundryCitationData;
    return parsed;
  } catch (error) {
    console.warn('Failed to parse Foundry citation ID', error);
    return null;
  }
};

const buildFoundrySourceUrl = (sourceUrl: string, pageNumber?: number | null): string => {
  if (!pageNumber || pageNumber < 1) {
    return sourceUrl;
  }

  const pageFragment = `page=${pageNumber}`;
  if (sourceUrl.includes('#')) {
    return `${sourceUrl}&${pageFragment}`;
  }
  return `${sourceUrl}#${pageFragment}`;
};

const buildCitationTooltip = (citationId: string, citationNumber: number): string => {
  const fallback = `View source (citation ${citationNumber})`;
  const foundryCitation = parseFoundryCitationId(citationId);
  if (!foundryCitation) {
    return fallback;
  }

  const title = foundryCitation.title?.trim();
  const page = foundryCitation.page_number;

  if (title && page && page > 0) {
    return `${title} (Page ${page})`;
  }
  if (title) {
    return title;
  }
  if (page && page > 0) {
    return `Source document (Page ${page})`;
  }

  return fallback;
};

/**
 * Parse message content and replace citations with CitationLink components.
 * Citations are numbered sequentially, with the same citation ID getting the same number.
 */
const parseCitations = (
  content: string,
  onCitationClick?: (citationId: string) => void
): (string | JSX.Element)[] => {
  if (!onCitationClick) {
    return [content];
  }

  const parts: (string | JSX.Element)[] = [];
  const citationIdToNumber = new Map<string, number>();
  let citationCounter = 0;
  let lastIndex = 0;
  let match;

  // Reset regex state
  CITATION_REGEX.lastIndex = 0;

  while ((match = CITATION_REGEX.exec(content)) !== null) {
    // Add text before the citation
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }

    const citationId = match[1];

    // Assign number (reuse if same citation ID appears multiple times)
    if (!citationIdToNumber.has(citationId)) {
      citationIdToNumber.set(citationId, ++citationCounter);
    }
    const citationNumber = citationIdToNumber.get(citationId)!;

    parts.push(
      <CitationLink
        key={`cite-${match.index}`}
        citationId={citationId}
        number={citationNumber}
        tooltip={buildCitationTooltip(citationId, citationNumber)}
        onClick={onCitationClick}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last citation
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts;
};

// Chat Message component
const ChatMessage: React.FC<{
  message: Message;
  isAssistant?: boolean;
  senderName?: string;
  isFirst?: boolean;
  onCopy?: (success: boolean) => void;
  conversationId?: string;
  messageIndex?: number;
  onCitationClick?: (citationId: string) => void;
}> = ({
  message,
  isAssistant = false,
  senderName = isAssistant ? 'Lupa' : 'User',
  isFirst = false,
  onCopy,
  conversationId,
  messageIndex,
  onCitationClick
}) => {
  const [isFeedbackOpen, setIsFeedbackOpen] = React.useState(false);

  // Parse citations for assistant messages
  const contentWithCitations = React.useMemo(() => {
    if (!isAssistant) {
      return [message.content];
    }
    return parseCitations(message.content, onCitationClick);
  }, [message.content, isAssistant, onCitationClick]);

  // Render content with bold formatting and citations
  const renderContent = () => {
    return contentWithCitations.map((part, partIndex) => {
      if (typeof part !== 'string') {
        // Already a React element (CitationLink)
        return part;
      }
      // Apply bold formatting to string parts
      return part.split('**').map((segment, segmentIndex) =>
        segmentIndex % 2 === 1
          ? <strong key={`${partIndex}-${segmentIndex}`}>{segment}</strong>
          : segment
      );
    });
  };

  return (
    <div style={{
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      marginTop: isFirst ? '1.5rem' : '0',
      justifyContent: 'flex-start',
      width: '100%'
    }}>
      <div style={{ width: '100%' }}>
        <div style={{
          backgroundColor: isAssistant ? 'transparent' : 'rgba(255, 255, 255, 0.14)',
          color: isAssistant ? 'rgba(255, 255, 255, 0.92)' : '#ffffff',
          padding: '0.9rem 1.1rem',
          borderRadius: '4px',
          border: isAssistant ? 'none' : '1px solid rgba(255, 255, 255, 0.22)',
          backdropFilter: isAssistant ? 'none' : 'blur(10px)',
          WebkitBackdropFilter: isAssistant ? 'none' : 'blur(10px)',
          boxShadow: isAssistant ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.12)',
          fontSize: '1rem',
          lineHeight: '1.5',
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Header with avatar and sender info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.9rem',
            marginBottom: '0.75rem'
          }}>
            <Avatar
              size={40}
              src={isAssistant ? BrandIcon : undefined}
              fallback={senderName.charAt(0)}
              color={isAssistant ? LUPA_AVATAR_BG : ACCENT_COLOR}
              borderColor={isAssistant ? ACCENT_COLOR : null}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{
                fontSize: '1.17rem',
                fontWeight: '600',
                color: isAssistant ? ACCENT_COLOR : '#ffffff',
                marginBottom: '0.2rem',
                lineHeight: 1.1
              }}>
                {senderName}
              </div>
              <div style={{
                fontSize: '0.833rem',
                color: isAssistant ? '#D4D4D4' : 'rgba(255, 255, 255, 0.75)'
              }}>
                {message.timestamp.toLocaleString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </div>
            </div>
            {isAssistant && (
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 0.78)',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = ACCENT_COLOR;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255, 255, 255, 0.78)';
                  }}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(message.content);
                      onCopy?.(true);
                    } catch (err) {
                      console.error('Failed to copy message:', err);
                      onCopy?.(false);
                    }
                  }}
                  aria-label="Copy message"
                  title="Copy message"
                >
                  <FontAwesomeIcon icon={faCopy} className="h-3.5 w-3.5" />
                </button>
                {conversationId && messageIndex !== undefined && (
                  <button
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.78)',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = ACCENT_COLOR;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255, 255, 255, 0.78)';
                    }}
                    onClick={() => setIsFeedbackOpen(true)}
                    aria-label="Report issue"
                    title="Report an issue with this response"
                  >
                    <FontAwesomeIcon icon={faFlag} className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Message content */}
          <div style={{ whiteSpace: 'pre-wrap', color: isAssistant ? '#D4D4D4' : '#ffffff', fontWeight: 300 }}>
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {isFeedbackOpen && conversationId && messageIndex !== undefined && (
        <FeedbackModal
          conversationId={conversationId}
          messageSequence={messageIndex}
          onClose={() => setIsFeedbackOpen(false)}
        />
      )}
    </div>
  );
};

// AI Disclaimer component with info tooltip
const AIDisclaimer: React.FC = () => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTooltip]);

  return (
    <div
      ref={tooltipRef}
      className="text-center mt-2 mb-1 relative"
      style={{
        maxWidth: '800px',
        width: '100%',
        margin: '0.5rem auto 0'
      }}
    >
      <button
        type="button"
        onClick={() => setShowTooltip(!showTooltip)}
        className="inline-flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
        style={{
          fontSize: '0.65rem',
          color: '#BFBFBF',
          fontFamily: 'Lato, system-ui, sans-serif',
          background: 'none',
          border: 'none',
          padding: 0
        }}
        aria-label="AI disclaimer information"
      >
        <span>AI responses may contain errors</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          style={{ width: '12px', height: '12px' }}
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px',
            backgroundColor: DARK_COLOR,
            color: 'white',
            padding: '12px 16px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontFamily: 'Lato, system-ui, sans-serif',
            lineHeight: '1.5',
            maxWidth: '300px',
            width: 'max-content',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            zIndex: 100,
            textAlign: 'left'
          }}
        >
          <p style={{ margin: 0, marginBottom: '8px', fontWeight: 500 }}>
            About AI Responses
          </p>
          <p style={{ margin: 0, opacity: 0.9 }}>
            This AI assistant uses machine learning to generate responses. While we strive for accuracy, responses may occasionally contain errors or outdated information. Always verify critical information with official sources.
          </p>
          {/* Tooltip arrow */}
          <div
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${DARK_COLOR}`
            }}
          />
        </div>
      )}
    </div>
  );
};

// Welcome Message component
const WelcomeMessage: React.FC<{ onTopicClick: (topic: string) => void; description: string; topics: string[]; disabled?: boolean }> = ({ onTopicClick, description, topics, disabled = false }) => {

  return (
    <div
      className="px-4 py-4 md:px-8 md:py-4"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
        textAlign: 'center'
      }}
    >
      {/* Main Welcome Message */}
      <div style={{
        marginBottom: '2rem',
        maxWidth: '800px'
      }}>
        <p
          className="text-base md:text-lg"
          style={{
            color: DARK_COLOR,
            lineHeight: '1.7',
            margin: '0 0 0 0',
            fontFamily: 'Lato, system-ui, sans-serif',
            fontWeight: '400',
            textAlign: 'left'
          }}
        >
          {description}
        </p>
      </div>

      {/* Suggested Topics Section */}
      <div style={{ maxWidth: '800px', width: '100%' }}>
        <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <h4 className="text-xs md:text-sm whitespace-nowrap" style={{
            fontWeight: '500',
            color: DARK_COLOR,
            margin: 0,
            fontFamily: 'Lato, system-ui, sans-serif'
          }}>
            Here are some topics to get you started
          </h4>
          <div style={{ flex: 1, height: '2px', borderRadius: '9999px', backgroundColor: ACCENT_COLOR }}></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
          {topics.map((topic, index) => (
            <button
              key={index}
              onClick={() => onTopicClick(topic)}
              disabled={disabled}
              className="text-xs md:text-sm px-2 py-2 md:px-4 md:py-3"
              style={{
                backgroundColor: DARK_COLOR,
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontWeight: '500',
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '2.5rem',
                fontFamily: 'Lato, system-ui, sans-serif',
                transition: 'background-color 0.2s',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
                lineHeight: '1.3',
                opacity: disabled ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!disabled) {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = ACCENT_COLOR;
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled) {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = DARK_COLOR;
                }
              }}
            >
              {topic}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

function App() {
  const { user, logout } = useAuth();
  // Conversations live in the app-wide Lupa store, shared with the floating
  // Lupa widget (contexts/LupaChatContext.tsx, 07-hubs §5).
  const {
    conversations,
    setConversations,
    currentConversationId,
    setCurrentConversationId,
    currentConversation,
    currentAgent,
    setCurrentAgent,
    selectedClientId,
    setSelectedClientId,
    isLoading,
    conversationsLoaded,
    sendMessage: sendToLupa,
    selectConversation,
  } = useLupaChat();
  const [input, setInput] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar state
  const [selectedCommunityLabel, setSelectedCommunityLabel] = useState<string>(
    currentConversation?.communityLabel ?? ''
  );
  const [conversationMenuAnchor, setConversationMenuAnchor] = useState<{
    id: string;
    top: number;
    left: number;
  } | null>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<{ top: number; left: number } | null>(null);
  const [currentSuggestion, setCurrentSuggestion] = useState<{ message: string; suggested_agent: string; suggested_agent_name: string; confidence: number } | null>(null);
  const [, setIsSubmitHover] = useState(false);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);
  const [showBetaWelcomeModal, setShowBetaWelcomeModal] = useState(false);
  const [showBetaWelcomePanel, setShowBetaWelcomePanel] = useState(false);
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // Citation document viewer state
  const [citationDocument, setCitationDocument] = useState<RagDocumentDetails | null>(null);
  const [citationChunkId, setCitationChunkId] = useState<string | undefined>(undefined);
  const toastTimeoutRef = useRef<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const conversationMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [pendingTitle, setPendingTitle] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const messages = useMemo(() => currentConversation?.messages ?? [], [currentConversation]);

  // ============================================================================
  // CENTRALIZED SESSION MANAGEMENT
  // ============================================================================
  // These refs provide a single source of truth for the current session/conversation
  // that closures can safely reference without capturing stale values.
  // ============================================================================

  // Ref that always holds the current session ID - closures read this at call time
  const currentSessionRef = useRef<string>(currentConversation?.sessionId ?? '');
  // Ref that always holds the current conversation ID - for validation
  const currentConversationRef = useRef<string>(currentConversationId);

  // Keep refs in sync with state (runs after every render where these change)
  useEffect(() => {
    if (currentConversation?.sessionId) {
      currentSessionRef.current = currentConversation.sessionId;
    }
    currentConversationRef.current = currentConversationId;
  }, [currentConversation?.sessionId, currentConversationId]);

  // Callback to get current session ID - safe to call from any closure
  const getCurrentSessionId = useCallback((): string => {
    return currentSessionRef.current || generateUUID();
  }, []);

  // Validate that a session ID matches the current session (for race condition detection)
  const isCurrentSession = useCallback((sessionId: string): boolean => {
    return sessionId === currentSessionRef.current;
  }, []);

  useEffect(() => {
    if (!currentConversation) {
      setSelectedCommunityLabel('');
      return;
    }
    const label = currentConversation.communityLabel ?? '';
    setSelectedCommunityLabel((prev) => (prev === label ? prev : label));
  }, [currentConversation]);

  // Check if user has seen beta welcome (tied to user ID)
  useEffect(() => {
    if (user?.id) {
      const betaWelcomeKey = `hasSeenBetaWelcome_${user.id}`;
      const hasSeenWelcome = localStorage.getItem(betaWelcomeKey);
      if (!hasSeenWelcome) {
        // Show the first-login modal
        setShowBetaWelcomeModal(true);
        // Mark as seen for this user
        localStorage.setItem(betaWelcomeKey, 'true');
      }
    }
  }, [user?.id]);

  // Note: Conversation persistence is handled by the backend /chat endpoint.
  // The backend saves conversations with user_id when processing messages.
  // Frontend only needs to sync conversation ID from backend response.

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  useEffect(() => {
    if (isRenamingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isRenamingTitle]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (
        conversationMenuRef.current &&
        !conversationMenuRef.current.contains(event.target as Node)
      ) {
        setConversationMenuAnchor(null);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuAnchor(null);
      }
    };

    if (dropdownOpen || conversationMenuAnchor || userMenuAnchor) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, conversationMenuAnchor, userMenuAnchor]);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        window.clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading || !currentConversation) return;
    setInput('');
    setCurrentSuggestion(null); // Clear any existing suggestion
    const response = await sendToLupa(content);
    // Check if there's a suggestion for a better agent
    if (response?._suggestion) {
      setCurrentSuggestion(response._suggestion);
    }
  };

  const handleTopicClick = (topic: string) => {
    // Capture session ID at click time to avoid race conditions
    // This ensures the message goes to the correct conversation
    if (!conversationsLoaded) {
      console.warn('Conversations not yet loaded, ignoring topic click');
      return;
    }
    sendMessage(topic);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const closeConversationMenu = () => {
    setConversationMenuAnchor(null);
  };

  const closeUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const startNewConversation = (
    agentId: string,
    communityLabel?: string,
    clientId?: string
  ) => {
    closeConversationMenu();
    const normalizedLabel =
      communityLabel && communityLabel.trim().length > 0 ? communityLabel : undefined;
    const newConversation = createEmptyConversation(agentId, normalizedLabel, clientId ?? selectedClientId);
    setConversations((prev) =>
      sortConversationsByUpdatedAt([newConversation, ...prev])
    );
    setCurrentConversationId(newConversation.id);
    setCurrentSuggestion(null);
    setInput('');
    setIsRenamingTitle(false);
    setPendingTitle('');
    // Don't change selectedCommunityLabel - it stays as the user's current selection
  };

  const clearMessages = () => {
    // New conversation inherits the currently selected community
    startNewConversation(currentAgent, selectedCommunityLabel || undefined);
  };

  const handleConversationSelect = (conversationId: string) => {
    selectConversation(conversationId);
    const selectedConversation = conversations.find(
      (conversation) => conversation.id === conversationId
    );
    if (selectedConversation) {
      setSelectedCommunityLabel(selectedConversation.communityLabel ?? '');
    }
    setCurrentSuggestion(null);
    setInput('');
    closeConversationMenu();
    setIsRenamingTitle(false);
    setPendingTitle('');
  };

  const renameConversation = (conversationId: string, title: string) => {
    const trimmed = title.trim();
    const safeTitle = trimmed || DEFAULT_CONVERSATION_TITLE;
    setConversations((prev) =>
      sortConversationsByUpdatedAt(
        prev.map((conversation) =>
          conversation.id === conversationId
            ? { ...conversation, title: safeTitle }
            : conversation
        )
      )
    );
  };

  const handleSuggestionSwitch = (agentId: string) => {
    setCurrentAgent(agentId);
    setConversations((prev) =>
      prev.map((conversation) =>
        conversation.id === currentConversationId
          ? {
              ...conversation,
              agentId,
            }
          : conversation
      )
    );
    setCurrentSuggestion(null);
    // Don't clear messages when switching via suggestion
  };

  const handleSuggestionDismiss = () => {
    setCurrentSuggestion(null);
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const toggleConversationMenu = (conversationId: string, anchorRect: DOMRect) => {
    setConversationMenuAnchor((prev) => {
      if (prev && prev.id === conversationId) {
        return null;
      }

      return {
        id: conversationId,
        top: anchorRect.top + window.scrollY + anchorRect.height / 2,
        left: anchorRect.right + window.scrollX + 12,
      };
    });
  };

  const toggleUserMenu = (anchorRect: DOMRect) => {
    setUserMenuAnchor((prev) => {
      if (prev) {
        return null;
      }

      if (userMenuAnchor) {
        setUserMenuAnchor(null);
        return null;
      }

      return {
        top: anchorRect.top + window.scrollY - 12,
        left: anchorRect.left + window.scrollX,
      };
    });
  };

  const beginRenameFromMenu = () => {
    if (!conversationMenuAnchor) return;
    const { id } = conversationMenuAnchor;
    const conversation = conversations.find((c) => c.id === id);
    if (!conversation) return;

    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c } : c)));
    setCurrentConversationId(id);
    setCurrentAgent(conversation.agentId);
    setPendingTitle(conversation.title);
    setIsRenamingTitle(true);
  };

  const archiveConversation = async (conversationId: string) => {
    closeConversationMenu();
    setIsRenamingTitle(false);
    setPendingTitle('');

    // Archive on backend (soft delete)
    let archived = false;
    try {
      archived = await apiService.deleteConversation(conversationId);
    } catch (error) {
      console.error('Failed to archive conversation:', error);
    }
    if (!archived) return;

    // Update local state - mark as archived instead of removing
    setConversations((prev) => {
      const updated = prev.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, isArchived: true }
          : conversation
      );

      // Get active (non-archived) conversations
      const activeConversations = updated.filter((c) => !c.isArchived);

      if (activeConversations.length === 0) {
        const fallback = createEmptyConversation(currentAgent, undefined, selectedClientId);
        setCurrentConversationId(fallback.id);
        setCurrentAgent(fallback.agentId);
        return [...updated, fallback];
      }

      if (currentConversationId === conversationId) {
        setCurrentConversationId(activeConversations[0].id);
        setCurrentAgent(activeConversations[0].agentId);
      }

      return updated;
    });
  };

  const restoreConversation = async (conversationId: string) => {
    const restored = await apiService.unarchiveConversation(conversationId);
    if (!restored) return;

    // Restore the conversation (unarchive it) and move to top of list
    setConversations((prev) => {
      const updated = prev.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, isArchived: false, updatedAt: new Date() }
          : conversation
      );
      return sortConversationsByUpdatedAt(updated);
    });
  };

  // Get archived conversations for the modal
  const archivedConversations = useMemo(
    () => conversations.filter((c) => c.isArchived),
    [conversations]
  );

  const showCopyToast = (success: boolean) => {
    setCopyToast(success ? 'Message copied to clipboard' : 'Copy failed');
    if (toastTimeoutRef.current) {
      window.clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = window.setTimeout(() => {
      setCopyToast(null);
      toastTimeoutRef.current = null;
    }, 2000);
  };

  const handleVoiceOpen = () => {
    setIsVoiceOpen(true);
  };

  const handleVoiceClose = () => {
    setIsVoiceOpen(false);
  };

  const handleBetaAnnouncementClick = () => {
    setShowBetaWelcomePanel(true);
    setSidebarOpen(false); // Close sidebar on mobile
  };

  /**
   * Handle citation click - looks up the chunk by azure_search_id,
   * fetches the document, and opens the DocumentViewerModal with the chunk highlighted.
   */
  const handleCitationClick = useCallback(async (citationId: string) => {
    try {
      const foundryCitation = parseFoundryCitationId(citationId);
      const lookupId = foundryCitation?.azure_search_id || citationId;

      // Look up the chunk by azure_search_id
      const chunkInfo = await apiService.getChunkByAzureSearchId(lookupId);
      if (chunkInfo) {
        // Get the document details
        const docDetails = await apiService.getRagDocumentDetails(chunkInfo.document_id, false);
        if (docDetails) {
          // Open the document viewer modal with the chunk highlighted
          // RagDocumentDetails extends RagDocument, so we can use it directly
          setCitationDocument(docDetails);
          setCitationChunkId(chunkInfo.azure_search_id);
          return;
        }
      }

      // Fallback for Foundry-only citations when chunk metadata isn't in local ingest DB.
      if (foundryCitation?.source_url) {
        const targetUrl = buildFoundrySourceUrl(
          foundryCitation.source_url,
          foundryCitation.page_number ?? undefined
        );
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      console.warn(`Citation not found: ${citationId}`);
    } catch (error) {
      console.error('Error handling citation click:', error);
    }
  }, []);

  const handleCitationModalClose = useCallback(() => {
    setCitationDocument(null);
    setCitationChunkId(undefined);
  }, []);

  const handleVoiceTranscript = (text: string) => {
    // When voice transcript is received, we could add it to the chat
    // For now we just log it - the VoiceInterface handles the full flow
    console.log('Voice transcript:', text);
  };

  const handleVoiceComplete = useCallback((
    transcript: string,
    response: string,
    sessionIdAtCallTime: string
  ) => {
    // Validate that the session hasn't changed during voice processing
    // This prevents messages from being added to the wrong conversation
    // if the user switched conversations while voice was processing
    if (!isCurrentSession(sessionIdAtCallTime)) {
      console.warn(
        `[VoiceComplete] Session mismatch detected. ` +
        `Voice was initiated with session ${sessionIdAtCallTime}, ` +
        `but current session is ${currentSessionRef.current}. ` +
        `Messages will be added to the conversation that initiated the voice request.`
      );
    }

    // Find the conversation by session ID (not current conversation)
    // This ensures messages go to the correct conversation even if user switched
    setConversations((prev) => {
      const targetConversation = prev.find(
        (conv) => conv.sessionId === sessionIdAtCallTime
      );

      if (!targetConversation) {
        console.error(
          `[VoiceComplete] Could not find conversation with session ${sessionIdAtCallTime}`
        );
        return prev;
      }

      const conversationId = targetConversation.id;
      const userTimestamp = new Date();
      const assistantTimestamp = new Date(userTimestamp.getTime() + 1);

      const userMessage: Message = {
        id: generateUUID(),
        role: 'user',
        content: transcript,
        timestamp: userTimestamp,
      };

      const assistantMessage: Message = {
        id: generateUUID(),
        role: 'assistant',
        content: response,
        timestamp: assistantTimestamp,
      };

      const updated = prev.map((conversation) => {
        if (conversation.id !== conversationId) {
          return conversation;
        }

        const isFirstMessage = conversation.messages.length === 0;
        const shouldAutoTitle =
          isFirstMessage && conversation.title === DEFAULT_CONVERSATION_TITLE;

        return {
          ...conversation,
          title: shouldAutoTitle
            ? deriveConversationTitle(transcript)
            : conversation.title,
          messages: [...conversation.messages, userMessage, assistantMessage],
          updatedAt: assistantTimestamp,
        };
      });

      return sortConversationsByUpdatedAt(updated);
    });
  }, [isCurrentSession, setConversations]);

  const getAgentDescription = (_agentId: string) => {
    const clientDesc: Record<string, string> = {
      'morningstar':
        "Hi, I'm Lupa. I'm wired into MorningStar's Aline CRM — ask me about occupancy, leads, move-ins, or how communities are trending.",
      'plc':
        "Hi, I'm Lupa. I'm wired into Priority Life Care's WelcomeHome CRM — ask me about leads, move-ins, occupancy by ownership group, or how Cortland / Reflections / other portfolios are performing.",
      'dwa-internal':
        "Hi, I'm Lupa. I'm wired into DWA's Teamwork timesheets and team pulse — ask me about project hours, who's at capacity, and how the team is trending.",
    };
    return clientDesc[selectedClientId]
      ?? "Hi, I'm Lupa! Pick a client in the dropdown to see what I can answer for them.";
  };

  const getAgentTopics = (_agentId: string) => {
    const clientTopics: Record<string, string[]> = {
      'morningstar': [
        "What's our current portfolio occupancy?",
        "How many move-ins did we have last week?",
        "Which communities have the most active leads?",
        "What are the top lead sources this month?",
        "Show me leads and tours over the last 8 weeks.",
        "How are we trending vs last year?",
      ],
      'plc': [
        "How many active leads do we have across PLC?",
        "What's portfolio occupancy by Ownership Group?",
        "Which communities had move-ins this week?",
        "What are the top lead sources this month?",
        "How did the Cortland communities perform last week?",
        "Show me move-outs and net change for the last 30 days.",
      ],
      'dwa-internal': [
        "What were the top projects by hours last week?",
        "Who logged the most hours this week?",
        "How is the team's pulse trending?",
        "Show me current active projects.",
        "What's our weekly capacity utilization?",
        "Compare this week's hours to last week.",
      ],
    };
    return clientTopics[selectedClientId] ?? [
      "What can you help me with?",
      "Which tools do you have access to?",
      "Show me a summary of recent activity.",
      "What data sources are connected?",
      "Help me get started.",
      "How do I switch clients?",
    ];
  };

  const currentConversationTitle = currentConversation?.title ?? DEFAULT_CONVERSATION_TITLE;
  const conversationCreatedAt = currentConversation
    ? new Date(currentConversation.createdAt)
    : null;

  const formattedConversationStart = conversationCreatedAt
    ? conversationCreatedAt.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : null;
  const CLIENT_LABELS: Record<string, string> = {
    'dwa-internal': 'Digital Wolf Agency',
    'morningstar': 'MorningStar Senior Living',
    'plc': 'Priority Life Care',
  };
  const displayedClientLabel = CLIENT_LABELS[selectedClientId] ?? null;

  const isClientLocked = (currentConversation?.messages?.length ?? 0) > 0;

  const beginTitleRename = () => {
    if (!currentConversation) return;
    setPendingTitle(currentConversation.title);
    setIsRenamingTitle(true);
  };

  const cancelTitleRename = () => {
    if (currentConversation) {
      setPendingTitle(currentConversation.title);
    } else {
      setPendingTitle('');
    }
    setIsRenamingTitle(false);
  };

  const commitTitleRename = async () => {
    if (!currentConversation) {
      setIsRenamingTitle(false);
      return;
    }

    const trimmed = pendingTitle.trim();
    const finalTitle = trimmed || currentConversation.title || DEFAULT_CONVERSATION_TITLE;

    // Persist to backend first
    const success = await apiService.updateConversationTitle(currentConversation.id, finalTitle);

    if (success) {
      renameConversation(currentConversation.id, finalTitle);
    } else {
      console.error('Failed to save conversation title to backend');
      // Still update local state for now, but log the error
      renameConversation(currentConversation.id, finalTitle);
    }

    setIsRenamingTitle(false);
    setPendingTitle('');
  };

  return (
    <>
    <div style={{
      height: '100%',
      overflow: 'hidden',
      display: 'flex', 
      background: 'linear-gradient(to top right, #000000 0%, #313131 100%)',
      fontFamily: 'Lato, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Sidebar */}
      <Sidebar
        conversations={conversations}
        currentConversationId={currentConversationId}
        dropdownOpen={dropdownOpen}
        toggleDropdown={toggleDropdown}
        dropdownRef={dropdownRef}
        onNewConversation={() => {
          clearMessages();
          setSidebarOpen(false); // Close sidebar on mobile after action
        }}
        onConversationSelect={(id) => {
          handleConversationSelect(id);
          setSidebarOpen(false); // Close sidebar on mobile after selection
        }}
        onConversationMenuToggle={toggleConversationMenu}
        onUserMenuToggle={toggleUserMenu}
        activeConversationMenuId={conversationMenuAnchor?.id ?? null}
        onVoiceClick={handleVoiceOpen}
        onBetaAnnouncementClick={handleBetaAnnouncementClick}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        selectedClientId={selectedClientId}
        isClientLocked={isClientLocked}
        onClientChange={(id) => {
          if (id !== selectedClientId) {
            setSelectedClientId(id);
            startNewConversation(currentAgent, undefined, id);
          }
          setSidebarOpen(false);
        }}
      />

      {/* Main Chat Area */}
      <div
        className="m-0 p-2 md:m-4 md:px-4 md:pb-4 md:pt-0 md:ml-0"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'transparent',
          position: 'relative',
          overflow: 'hidden',
          fontFamily: 'Lato, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
        {/* Mobile Hamburger Menu Button */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="md:hidden absolute top-7 left-3 z-40 p-2 flex items-center justify-center"
          aria-label="Open menu"
          style={{ background: 'none', border: 'none' }}
        >
          <FontAwesomeIcon icon={faBars} className="h-5 w-5" style={{ color: DARK_COLOR }} />
        </button>


        {/* Main Chat Card */}
        <div
          className="mt-8 md:mt-0"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '1080px',
            width: '100%',
            margin: '0 auto',
            background: 'rgba(25, 25, 25, 0.6)',
            backdropFilter: 'blur(10px)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            overflow: 'hidden'
          }}>
          {/* Header */}
          <div style={{
            padding: '1.5rem 2rem 1rem',
            borderBottom: '1px solid rgba(229, 231, 235, 0.5)',
            position: 'relative'
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              textAlign: 'center',
              gap: '0.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                {isRenamingTitle ? (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      commitTitleRename();
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <input
                      ref={titleInputRef}
                      value={pendingTitle}
                      onChange={(event) => setPendingTitle(event.target.value)}
                      onBlur={commitTitleRename}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          cancelTitleRename();
                        }
                      }}
                      style={{
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: DARK_COLOR,
                        border: `1px solid ${DEFAULT_BORDER_COLOR}`,
                        borderRadius: '9999px',
                        padding: '0.25rem 0.75rem',
                        fontFamily: 'Lato, system-ui, sans-serif',
                        outline: 'none',
                        minWidth: '160px'
                      }}
                      aria-label="Rename conversation"
                    />
                  </form>
                ) : (
                  <>
                    <h2 style={{ 
                      fontSize: '1rem', 
                      fontWeight: '600', 
                      color: '#ffffff', 
                      margin: 0,
                      fontFamily: 'Lato, system-ui, sans-serif'
                    }}>
                      {currentConversationTitle}
                    </h2>
                    <button
                      type="button"
                      onClick={beginTitleRename}
                      className="ml-1 mt-1"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      aria-label="Edit conversation title"
                    >
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="h-3 w-3 text-[#444444] transition-colors duration-200 hover:text-[var(--color-accent)]"
                      />
                    </button>
                  </>
                )}
              </div>
              
              {(formattedConversationStart || displayedClientLabel) && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  alignItems: 'center'
                }}>
                  {formattedConversationStart && (
                    <p style={{
                      fontSize: '0.75rem',
                      color: '#ffffff',
                      margin: 0,
                      fontFamily: 'Lato, system-ui, sans-serif',
                      fontWeight: '400'
                    }}>
                      Started on {formattedConversationStart}
                    </p>
                  )}
                  {displayedClientLabel && (
                    <p style={{
                      fontSize: '0.75rem',
                      color: ACCENT_COLOR,
                      margin: 0,
                      fontFamily: 'Lato, system-ui, sans-serif',
                      fontWeight: '600',
                      letterSpacing: '0.02em',
                    }}>
                      {displayedClientLabel}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className="chat-scroll-area py-0 px-8 md:px-24" style={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative'
          }}>
            {messages.length === 0 ? (
              <WelcomeMessage
                onTopicClick={handleTopicClick}
                description={getAgentDescription(currentAgent)}
                topics={getAgentTopics(currentAgent)}
                disabled={!conversationsLoaded}
              />
            ) : (
              <div>
                {messages.map((message, index) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    isAssistant={message.role === 'assistant'}
                    senderName={message.role === 'assistant' ? 'Lupa' : 'User'}
                    isFirst={index === 0}
                    onCopy={showCopyToast}
                    conversationId={currentConversation?.id}
                    messageIndex={index}
                    onCitationClick={handleCitationClick}
                  />
                ))}
                
                {/* Agent Suggestion */}
                {currentSuggestion && (
                  <AgentSuggestionComponent
                    suggestion={currentSuggestion}
                    onSwitchAgent={handleSuggestionSwitch}
                    onDismiss={handleSuggestionDismiss}
                  />
                )}
                
                {isLoading && (
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div style={{
                      backgroundColor: 'white',
                      color: DARK_COLOR,
                      padding: '0.75rem 1rem',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      maxWidth: 'fit-content'
                    }}>
                      {/* Header with avatar and sender info */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                        marginBottom: '0.5rem'
                      }}>
                        <Avatar size={28} color={LUPA_AVATAR_BG} borderColor={ACCENT_COLOR} />
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            color: DARK_COLOR,
                            marginBottom: '0.125rem'
                          }}>
                            Lupa
                          </div>
                          <div style={{
                            fontSize: '0.7rem',
                            color: DARK_COLOR
                          }}>
                            {new Date().toLocaleString('en-US', { 
                              month: 'long', 
                              day: 'numeric', 
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                      
                      {/* Typing indicator */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#9ca3af',
                            borderRadius: '50%',
                            animation: 'bounce 1s infinite'
                          }}></div>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#9ca3af',
                            borderRadius: '50%',
                            animation: 'bounce 1s infinite 0.1s'
                          }}></div>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#9ca3af',
                            borderRadius: '50%',
                            animation: 'bounce 1s infinite 0.2s'
                          }}></div>
                        </div>
                        <span style={{ fontSize: '0.8rem', color: DARK_COLOR }}>Lupa is typing...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Input Area - Separate Card */}
        <div
          ref={inputContainerRef}
          className="chat-input-shell"
          style={{
            padding: '0.25rem 0.5rem',
            background: 'rgba(25, 25, 25, 0.6)',
            backdropFilter: 'blur(10px)',
            borderRadius: '4px',
            border: `1px solid ${CHAT_SHELL_BORDER_COLOR}`,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            marginTop: '1rem',
            maxWidth: '1080px',
            width: '100%',
            margin: '1rem auto 0',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = ACCENT_COLOR;
          }}
          onMouseLeave={(e) => {
            if (chatInputRef.current !== document.activeElement) {
              e.currentTarget.style.borderColor = CHAT_SHELL_BORDER_COLOR;
              e.currentTarget.style.boxShadow = CARD_SHADOW;
            }
          }}
        >
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <div style={{ position: 'relative', width: '100%' }}>
                <button
                  type="button"
                  onClick={handleVoiceOpen}
                  style={{
                    position: 'absolute',
                    left: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                    borderRadius: '50%',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(254, 195, 92, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  aria-label="Open voice chat"
                  title="Voice chat"
                >
                  <FontAwesomeIcon
                    icon={faMicrophone}
                    style={{ color: DARK_COLOR, width: '16px', height: '16px', transition: 'color 0.2s ease' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as SVGSVGElement).style.color = ACCENT_COLOR;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as SVGSVGElement).style.color = DARK_COLOR;
                    }}
                  />
                </button>
                
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '0',
                    borderRadius: '9999px',
                    width: '48px',
                    height: '44px',
                    backgroundColor: ACCENT_COLOR,
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '500',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
                    zIndex: 1,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    paddingLeft: '0',
                    paddingRight: '0'
                  }}
                  onMouseEnter={(e) => {
                    setIsSubmitHover(true);
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = '#f16952';
                      e.currentTarget.style.transform = 'translateY(-50%)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    setIsSubmitHover(false);
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor = ACCENT_COLOR;
                      e.currentTarget.style.transform = 'translateY(-50%)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onFocus={() => setIsSubmitHover(true)}
                  onBlur={() => setIsSubmitHover(false)}
                >
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="h-4 w-4 text-white"
                  />
                </button>

                <input
                  ref={chatInputRef}
                  className="chat-input-field"
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask me anything"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    padding: '0.875rem 4.5rem 0.875rem 2.5rem',
                    borderRadius: '4px',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '0.875rem',
                    fontFamily: 'Lato, system-ui, sans-serif',
                    transition: 'color 0.2s ease',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    height: '52px',
                    boxSizing: 'border-box'
                  }}
                  onFocus={() => {
                    if (inputContainerRef.current) {
                      inputContainerRef.current.style.borderColor = ACCENT_COLOR;
                      inputContainerRef.current.style.boxShadow = FOCUS_RING;
                    }
                  }}
                  onBlur={() => {
                    if (inputContainerRef.current) {
                      if (!inputContainerRef.current.matches(':hover')) {
                        inputContainerRef.current.style.borderColor = CHAT_SHELL_BORDER_COLOR;
                        inputContainerRef.current.style.boxShadow = CARD_SHADOW;
                      } else {
                        inputContainerRef.current.style.boxShadow = CARD_SHADOW;
                      }
                    }
                  }}
                />
              </div>
            </form>
          </div>

          {/* AI Disclaimer */}
          <AIDisclaimer />

      {/* Footer Links - Fixed to bottom right */}
      {/* Footer links - hidden on mobile to avoid overlap with input */}
      <div
        className="hidden md:flex"
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          alignItems: 'center',
          gap: '8px',
          justifyContent: 'flex-end',
          zIndex: 5
        }}
      >
        <button
          type="button"
          onClick={() => setShowTermsModal(true)}
          style={{
            fontSize: '0.7rem',
            color: '#BFBFBF',
            textDecoration: 'none',
            fontFamily: 'Lato, system-ui, sans-serif',
            fontWeight: 500,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.textDecoration = 'none';
          }}
        >
          Terms
        </button>
        <span style={{
          fontSize: '0.7rem',
          color: '#BFBFBF',
          fontFamily: 'Lato, system-ui, sans-serif'
        }}>
          |
        </span>
        <button
          type="button"
          onClick={() => setShowPrivacyModal(true)}
          style={{
            fontSize: '0.7rem',
            color: '#BFBFBF',
            textDecoration: 'none',
            fontFamily: 'Lato, system-ui, sans-serif',
            fontWeight: 500,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.textDecoration = 'none';
          }}
        >
          Privacy
        </button>
      </div>
    </div>
    {conversationMenuAnchor &&
      createPortal(
        <div
          ref={conversationMenuRef}
          style={{
            position: 'absolute',
            top: conversationMenuAnchor.top,
            left: conversationMenuAnchor.left,
            transform: 'translateY(-50%)',
            zIndex: 3000,
            backgroundColor: '#444444',
            borderRadius: '4px',
            padding: '6px 0',
            minWidth: '150px',
            boxShadow: '0 20px 45px -20px rgba(15,23,42,0.45)',
            overflow: 'hidden'
          }}
        >
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              beginRenameFromMenu();
              closeConversationMenu();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                beginRenameFromMenu();
                closeConversationMenu();
              }
            }}
            style={{
              padding: '6px 14px',
              color: 'white',
              fontSize: '0.8rem',
              fontFamily: 'Lato, system-ui, sans-serif',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              display: 'block'
            }}
            onMouseEnter={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(234, 130, 102, 0.9)';
            }}
            onMouseLeave={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
            }}
          >
            Rename
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              if (conversationMenuAnchor) {
                archiveConversation(conversationMenuAnchor.id);
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (conversationMenuAnchor) {
                  archiveConversation(conversationMenuAnchor.id);
                }
              }
            }}
            style={{
              padding: '6px 14px',
              color: 'white',
              fontSize: '0.8rem',
              fontFamily: 'Lato, system-ui, sans-serif',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              display: 'block'
            }}
            onMouseEnter={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(234, 130, 102, 0.9)';
            }}
            onMouseLeave={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
            }}
          >
            Archive
          </div>
        </div>,
        document.body
      )}
    {userMenuAnchor &&
      createPortal(
        <div
          ref={userMenuRef}
          style={{
            position: 'absolute',
            top: userMenuAnchor.top,
            left: userMenuAnchor.left,
            transform: 'translate(0, -100%)',
            zIndex: 3000,
            backgroundColor: '#444444',
            borderRadius: '4px',
            padding: '8px 0',
            minWidth: '170px',
            boxShadow: '0 20px 45px -20px rgba(15,23,42,0.45)',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '6px 18px',
              color: 'white',
              fontSize: '0.8rem',
              fontFamily: 'Lato, system-ui, sans-serif',
              display: 'block',
              textAlign: 'left'
            }}
          >
            {user?.display_name || user?.email || 'User'}
          </div>
          <div
            style={{
              height: '6px'
            }}
          />
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              closeUserMenu();
              setShowSettingsModal(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                closeUserMenu();
                setShowSettingsModal(true);
              }
            }}
            style={{
              padding: '6px 18px',
              color: 'white',
              fontSize: '0.8rem',
              fontFamily: 'Lato, system-ui, sans-serif',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              display: 'block',
              textAlign: 'left'
            }}
            onMouseEnter={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(234, 130, 102, 0.9)';
            }}
            onMouseLeave={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
            }}
          >
            Settings
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              closeUserMenu();
              setShowArchivedModal(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                closeUserMenu();
                setShowArchivedModal(true);
              }
            }}
            style={{
              padding: '6px 18px',
              color: 'white',
              fontSize: '0.8rem',
              fontFamily: 'Lato, system-ui, sans-serif',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              display: 'block',
              textAlign: 'left'
            }}
            onMouseEnter={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(234, 130, 102, 0.9)';
            }}
            onMouseLeave={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
            }}
          >
            View Archived
          </div>
          {user?.role === 'dwa_admin' && (
            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                closeUserMenu();
                window.open('/lupa-admin', '_blank');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  closeUserMenu();
                  window.open('/lupa-admin', '_blank');
                }
              }}
              style={{
                padding: '6px 18px',
                color: 'white',
                fontSize: '0.8rem',
                fontFamily: 'Lato, system-ui, sans-serif',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                textAlign: 'left'
              }}
              onMouseEnter={(event) => {
                (event.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(234, 130, 102, 0.9)';
              }}
              onMouseLeave={(event) => {
                (event.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
              }}
            >
              Lupa Admin
              <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>↗</span>
            </div>
          )}
          <div
            role="button"
            tabIndex={0}
            onClick={async () => {
              closeUserMenu();
              await logout();
            }}
            onKeyDown={async (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                closeUserMenu();
                await logout();
              }
            }}
            style={{
              padding: '6px 18px',
              color: 'white',
              fontSize: '0.8rem',
              fontFamily: 'Lato, system-ui, sans-serif',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              display: 'block',
              textAlign: 'left'
            }}
            onMouseEnter={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(234, 130, 102, 0.9)';
            }}
            onMouseLeave={(event) => {
              (event.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
            }}
          >
            Sign Out
          </div>
        </div>,
        document.body
      )}
    {copyToast && (
      <div
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#444444',
          color: 'white',
          padding: '0.6rem 1.2rem',
          borderRadius: '9999px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          fontSize: '0.8rem',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          zIndex: 2000
        }}
      >
        <FontAwesomeIcon icon={faCopy} className="h-3 w-3 text-white" />
        {copyToast}
      </div>
    )}
    </div>

    {/* Voice Interface Modal */}
    <VoiceInterface
      isOpen={isVoiceOpen}
      onClose={handleVoiceClose}
      agentId={currentAgent}
      getSessionId={getCurrentSessionId}
      onTranscript={handleVoiceTranscript}
      onVoiceComplete={handleVoiceComplete}
      clientId={selectedClientId}
    />

    {/* Beta Welcome Modal - First Login */}
    <BetaWelcomeModal
      isOpen={showBetaWelcomeModal}
      onClose={() => setShowBetaWelcomeModal(false)}
      onViewAnnouncement={() => setShowBetaWelcomePanel(true)}
    />

    {/* Beta Welcome Panel - Full Announcement */}
    <BetaWelcomePanel
      isOpen={showBetaWelcomePanel}
      onClose={() => setShowBetaWelcomePanel(false)}
    />

    {/* Terms Modal */}
    <TermsModal
      isOpen={showTermsModal}
      onClose={() => setShowTermsModal(false)}
    />

    {/* Privacy Modal */}
    <PrivacyModal
      isOpen={showPrivacyModal}
      onClose={() => setShowPrivacyModal(false)}
    />

    {/* Settings Modal */}
    <SettingsModal
      isOpen={showSettingsModal}
      onClose={() => setShowSettingsModal(false)}
      user={user}
    />

    {/* Archived Conversations Modal */}
    <ArchivedModal
      isOpen={showArchivedModal}
      onClose={() => setShowArchivedModal(false)}
      archivedConversations={archivedConversations}
      onRestoreConversation={restoreConversation}
    />

    {/* Citation Document Viewer Modal */}
    {citationDocument && (
      <DocumentViewerModal
        document={citationDocument}
        onClose={handleCitationModalClose}
        initialChunkId={citationChunkId}
      />
    )}

    </>
  );
}

export default App;
